/**
 * DemoToolbar — floating widget for the live demo.
 *
 * Renders on every authenticated screen and offers three affordances:
 *   1. A small "you're in demo mode" banner at the very top of the page
 *      (dismissible per-session).
 *   2. A persona switcher (Parent / Caregiver / Admin) — instant-swap via
 *      POST /api/demo/login-as/{persona}. Refreshes the auth state by
 *      writing the new token to localStorage and calling /auth/me.
 *   3. A "Take the tour" CTA that opens the GuidedTour overlay.
 *
 * Honest about demo data: copy never pretends. The banner makes it clear
 * everything is fictional and any action is reversible.
 *
 * Hidden when:
 *   - User is not authenticated.
 *   - User is in the kiosk surface (/with/, /panel, /signage).
 *   - User has dismissed for this session.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeftRight, X, Play } from "lucide-react";
import { api, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import GuidedTour from "./GuidedTour";

const ROLE_TILES = [
  { key: "parent",    label: "Parent",    name: "Eliana", to: "/home",      tone: "bg-[#F4E5C5]/60 text-[#8C6D1F]" },
  { key: "caregiver", label: "Caregiver", name: "Mira",   to: "/staff",     tone: "bg-[#E6EFE8] text-[#1F3A2A]" },
  { key: "admin",     label: "Admin",     name: "Aanya",  to: "/admin",     tone: "bg-[#D1F1D6] text-[#1F3A2A]" },
];

const SESSION_KEY = "alouvya_demo_banner_dismissed";
const TOUR_KEY = "alouvya_demo_tour_completed";

export default function DemoToolbar() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [switching, setSwitching] = useState(null);

  // Honor dismiss state.
  useEffect(() => {
    setBannerDismissed(sessionStorage.getItem(SESSION_KEY) === "1");
  }, []);

  // Auto-launch tour on first visit (once per browser).
  useEffect(() => {
    if (!user) return;
    if (loc.pathname !== "/home") return;
    if (localStorage.getItem(TOUR_KEY) === "1") return;
    // Tiny delay so the page mounts first.
    const id = window.setTimeout(() => setTourOpen(true), 900);
    return () => window.clearTimeout(id);
  }, [user, loc.pathname]);

  // Hide on kiosk / unauthenticated / signage.
  const hideOnPaths = ["/with/", "/panel", "/signage", "/signin", "/signup", "/forgot-password", "/reset-password"];
  if (!user) return null;
  if (hideOnPaths.some((p) => loc.pathname.startsWith(p))) return null;

  async function switchTo(persona) {
    setSwitching(persona.key);
    try {
      const r = await api.post(`/demo/login-as/${persona.key}`);
      setToken(r.data.token);
      // Refresh the auth state by hitting /auth/me with the new token.
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(`Now viewing as ${persona.label} · ${persona.name}.`);
      setSwitcherOpen(false);
      nav(persona.to);
    } catch (e) {
      toast.error("Could not switch persona. Please try again.");
    } finally {
      setSwitching(null);
    }
  }

  return (
    <>
      {/* Top banner */}
      {!bannerDismissed && (
        <div
          data-testid="demo-banner"
          className="fixed top-0 inset-x-0 z-40 bg-[#1F3A2A] text-white text-xs sm:text-sm py-2 px-4 flex items-center justify-center gap-3 shadow-[0_4px_12px_rgba(31,58,42,0.18)]"
        >
          <Sparkles size={14} strokeWidth={2} className="hidden sm:block opacity-90" />
          <span>
            <span className="font-semibold">{t("admin_panel.demo_toolbar.headline")}</span> {t("admin_panel.demo_toolbar.body")}
          </span>
          <button
            type="button"
            data-testid="demo-toolbar-open-switcher"
            onClick={() => setSwitcherOpen(true)}
            className="ml-2 inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1 transition-colors"
          >
            <ArrowLeftRight size={12} />
            {t("admin_panel.demo_toolbar.switch_persona")}
          </button>
          <button
            type="button"
            data-testid="demo-toolbar-open-tour"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1 transition-colors"
          >
            <Play size={12} />
            {t("admin_panel.demo_toolbar.take_the_tour")}
          </button>
          <button
            type="button"
            aria-label={t("admin_panel.demo_toolbar.dismiss")}
            data-testid="demo-banner-dismiss"
            onClick={() => {
              sessionStorage.setItem(SESSION_KEY, "1");
              setBannerDismissed(true);
            }}
            className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Floating switcher (bottom-right) — also opens via banner button */}
      {bannerDismissed && (
        <button
          type="button"
          data-testid="demo-toolbar-floating-trigger"
          onClick={() => setSwitcherOpen(true)}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white px-5 py-3 text-sm shadow-[0_12px_28px_rgba(31,58,42,0.32)] hover:bg-[#132C17] transition-colors"
        >
          <Sparkles size={14} />
          {t("admin_panel.demo_toolbar.floating_pill")}
        </button>
      )}

      {/* Switcher modal */}
      {switcherOpen && (
        <div
          data-testid="demo-persona-switcher"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F2018]/55 backdrop-blur-sm"
          onClick={() => setSwitcherOpen(false)}
        >
          <div
            className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_24px_48px_rgba(31,58,42,0.32)] border border-[#EEF0F4] p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="demo-persona-switcher-close"
              aria-label="Close"
              onClick={() => setSwitcherOpen(false)}
              className="absolute top-4 right-4 text-[#6B7280] hover:text-[#1A1B2E] p-1.5"
            >
              <X size={18} />
            </button>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
              Switch demo persona
            </div>
            <h2 className="mt-3 font-display text-2xl tracking-tight text-[#1A1B2E]">
              See it from another seat.
            </h2>
            <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">
              Alouvya looks calmer when you see the same day through every role.
            </p>
            <div className="mt-6 space-y-3">
              {ROLE_TILES.map((tile) => {
                const active = user?.role === tile.key;
                return (
                  <button
                    key={tile.key}
                    type="button"
                    data-testid={`demo-persona-tile-${tile.key}`}
                    disabled={active || switching !== null}
                    onClick={() => switchTo(tile)}
                    className={
                      "w-full flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-all " +
                      (active
                        ? "bg-[#E6EFE8] border-[#1F3A2A] cursor-default"
                        : "bg-white border-[#EEF0F4] hover:border-[#1F3A2A] hover:shadow-[0_4px_12px_rgba(16,24,40,0.08)]")
                    }
                  >
                    <div>
                      <div className={`inline-flex items-center text-[10px] tracking-[0.22em] uppercase font-semibold rounded-full px-2 py-0.5 ${tile.tone}`}>
                        {tile.label}
                      </div>
                      <div className="mt-1.5 font-display text-lg text-[#1A1B2E]">{tile.name}</div>
                    </div>
                    {active ? (
                      <span className="text-xs text-[#1F3A2A] font-semibold">Current</span>
                    ) : switching === tile.key ? (
                      <span className="text-xs text-[#6B7280]">Switching…</span>
                    ) : (
                      <ArrowLeftRight size={16} className="text-[#6B7280]" />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-6 text-[11px] text-[#9CA3AF] leading-relaxed">
              No data is changed when you switch. The personas share the same workspace (Maple Grove). Sign-in tokens are reissued instantly.
            </p>
          </div>
        </div>
      )}

      <GuidedTour
        open={tourOpen}
        userRole={user.role}
        onClose={() => {
          localStorage.setItem(TOUR_KEY, "1");
          setTourOpen(false);
        }}
      />
    </>
  );
}
